import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiClient, platformAPI } from '../../services/api';
import { AdminTableCard } from '../AdminSurface';

type BillingRevenueTrendSource = 'tenant' | 'platform';

type BillingRevenueTrendChartProps = {
  className?: string;
  source?: BillingRevenueTrendSource;
  companyId?: string;
  showFeeComparison?: boolean;
  days?: number;
};

type TrendInvoice = {
  amount_paid?: number | string | null;
  total?: number | string | null;
  platform_fee?: number | string | null;
  paid_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export default function BillingRevenueTrendChart({
  className,
  source = 'tenant',
  companyId,
  showFeeComparison = false,
  days = 14,
}: BillingRevenueTrendChartProps) {
  const { data: paidInvoices = [], isLoading } = useQuery<TrendInvoice[]>(
    ['billing-revenue-trend', source, companyId, days],
    async () => {
      if (source === 'platform') {
        const response = await platformAPI.getInvoices({
          status: 'paid',
          companyId: companyId || undefined,
          page: 1,
          limit: 500,
        });
        return Array.isArray(response?.data?.invoices) ? response.data.invoices : [];
      }

      const response = await apiClient.get('/invoices/admin/all', {
        params: {
          status: 'paid',
          page: 1,
          limit: 500,
        },
      });

      return Array.isArray(response?.data?.invoices) ? response.data.invoices : [];
    },
    {
      staleTime: 60_000,
      keepPreviousData: true,
    },
  );

  const trendData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dayBuckets = new Map<string, { day: string; label: string; revenue: number; fees: number }>();
    const dayOrder: string[] = [];

    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);

      const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dayOrder.push(dayKey);
      dayBuckets.set(dayKey, {
        day: dayKey,
        label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: 0,
        fees: 0,
      });
    }

    paidInvoices.forEach((invoice) => {
      const paidAtRaw = invoice.paid_at || invoice.updated_at || invoice.created_at;
      if (!paidAtRaw) return;

      const paidDate = new Date(paidAtRaw);
      if (Number.isNaN(paidDate.getTime())) return;

      const dayKey = `${paidDate.getFullYear()}-${String(paidDate.getMonth() + 1).padStart(2, '0')}-${String(paidDate.getDate()).padStart(2, '0')}`;
      const bucket = dayBuckets.get(dayKey);
      if (!bucket) return;

      const revenueCents = toNumber(invoice.amount_paid) || toNumber(invoice.total);
      const feeCents = toNumber(invoice.platform_fee);

      bucket.revenue += revenueCents / 100;
      bucket.fees += feeCents / 100;
    });

    return dayOrder.map((dayKey) => dayBuckets.get(dayKey)!);
  }, [days, paidInvoices]);

  const trendTotals = useMemo(() => {
    return trendData.reduce(
      (acc, day) => {
        acc.revenue += day.revenue;
        acc.fees += day.fees;
        return acc;
      },
      { revenue: 0, fees: 0 },
    );
  }, [trendData]);

  return (
    <AdminTableCard
      className={className}
      title={showFeeComparison ? `Revenue vs Fees (Past ${days} Days)` : `Revenue Trend (Past ${days} Days)`}
      description={showFeeComparison
        ? 'Revenue tracks paid invoice amounts, and fees track collected platform fees.'
        : 'Revenue tracks paid invoice amounts over the selected period.'}
      meta={
        <div className="text-right">
          <p className="text-xs text-on-surface-variant">{days}-day revenue</p>
          <p className="text-sm font-semibold text-on-surface">{formatCurrency(trendTotals.revenue)}</p>
          {showFeeComparison && (
            <>
              <p className="mt-1 text-xs text-on-surface-variant">{days}-day fees</p>
              <p className="text-sm font-semibold text-on-surface">{formatCurrency(trendTotals.fees)}</p>
            </>
          )}
        </div>
      }
    >
      <div className="h-80 px-6 pb-6">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-on-surface-variant">Loading trend data...</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis tickFormatter={(value: number) => `$${value.toFixed(0)}`} />
              <Tooltip
                formatter={(value: number | string, name: string) => [
                  formatCurrency(typeof value === 'number' ? value : Number(value)),
                  name === 'fees' ? 'Fees Collected' : 'Revenue',
                ]}
              />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#16a34a" radius={[4, 4, 0, 0]} />
              {showFeeComparison && (
                <Bar dataKey="fees" name="Fees Collected" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </AdminTableCard>
  );
}